/**
 * 型の登録（索引 Issue #498 の段階 2。設計文書 §0・§5）。
 *
 * 型 = **帯の仕様**。演出（テーマの `effects/`）から `editorPorts/duelEffects.js::duelSpecs` が作り、レジストリに集める。
 * **描き方（`Component` / `draw` / `three` / `render`）をここに書いたら例外**にする —— それが「ロジックと描画を分けた」の
 * 中身そのもので、仕様に描き方が混ざった瞬間にコアが React や three を知ることになる。描き方は舞台
 * （`editorPorts/duelRenderer.js` → `duelEditor/stage/`）が同じ `id` で引く。
 *
 * 型が宣言できるのは「いつ出るか（`opens` / `closes` / `build`）」「どれだけ出るか（`timing`）」
 * 「どう並ぶか（`overlaps` / `overlapKey` / `layer` / `track`）」「何を鳴らすか（`sounds`）」だけ。
 */
/**
 * 帯の出方。
 * - `event`  イベントに合わせて出て、決まった尺で閉じる
 * - `state`  **可変尺で出しっぱなし**（終わりは後ろのアンカーが決める）。タイムラインの帯ではなく
 *   **状態チャンネル**として扱う（`states.js`）。`persistent` は古い名前（別名）
 */
const LAYERS = ['event', 'state', 'persistent'];
export const isStateLayer = (layer) => layer === 'state' || layer === 'persistent';
const ID = /^[a-z0-9_]{1,40}$/;
const DRAW_KEYS = ['Component', 'draw', 'three', 'render', 'element'];

const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/** 型の宣言を検めて足りないところを補う。壊れた宣言はここで分かるように投げる */
export function defineType(spec, where = 'type') {
  const e = spec?.default ?? spec;
  if (!isObj(e)) throw new Error(`${where}: 型の宣言ではありません`);
  if (!ID.test(e.id ?? '')) throw new Error(`${where}: id は英小文字・数字・_（${e.id}）`);
  const at = `${where}(${e.id})`;
  for (const k of DRAW_KEYS) {
    if (e[k] != null) throw new Error(`${at}: 描き方（${k}）は型の宣言に書けません。舞台の側に置いてください`);
  }
  if (typeof e.build !== 'function' && typeof e.opens !== 'function') throw new Error(`${at}: build か opens が要ります`);
  // 帯ごとの重ねの鍵（`generate.js`）。data から名前を返す関数
  if (e.overlapKey != null && typeof e.overlapKey !== 'function') throw new Error(`${at}: overlapKey は関数です（data → 重ねの鍵）`);
  if (e.layer != null && !LAYERS.includes(e.layer)) throw new Error(`${at}: layer は ${LAYERS.join(' / ')}（${e.layer}）`);
  // 閉じるアンカーで終わる帯は**状態**だけ。整列は前向きの 1 パスなので、終わりが後ろのアンカーで決まる帯を並べられない
  if (typeof e.closes === 'function' && !isStateLayer(e.layer)) throw new Error(`${at}: closes を持てるのは layer: 'state' だけです`);
  return {
    label: e.id,
    track: 'graphic',
    layer: 'event',
    color: null,
    overlaps: {},
    ...e,
    timing: { durationMs: 1000, endOffsetMs: 0, ...(e.timing ?? {}) },
  };
}

/** 型の一覧。**登録順が、トラックが同じときの描く順の決め手**になる */
export function createRegistry(specs = []) {
  const list = specs.map((s, i) => defineType(s, `types[${i}]`));
  const byId = new Map(list.map((s) => [s.id, s]));
  const order = new Map(list.map((s, i) => [s.id, i]));
  return {
    has: (id) => byId.has(id),
    get: (id) => byId.get(id) ?? null,
    list: () => [...list],
    order: (id) => order.get(id) ?? Number.MAX_SAFE_INTEGER,
  };
}

export const EMPTY_REGISTRY = createRegistry([]);
